import { useCallback, useMemo, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  loginSuccess,
  logout as logoutAction,
  setLoading,
  updateUser,
  updateRole,
  updatePermissions,
} from "@/store/slices/authSlice";
import {
  selectIsAuthenticated,
  selectIsLoading,
  selectUser,
  selectUserRole,
  selectUserPermissions,
  selectUserPermissionObjects,
  selectIsSystemAdmin,
  selectIssuperUser,
} from "@/store/selectors";
import { Permissions, ApiPermissionsResponse } from "@/types/permissions";
import { setAuth, clearAuth } from "@/services/auth";
import { cancelAllRequests } from "@/src/api/api";
import { AppThunkDispatch } from "@/store";
import { useDispatch } from "react-redux";
import actionsHandler from "@/store/actions/actionHandler";
import { ADMIN_ROLE } from "@/constants/constants";
import { setLanguage } from "@/store/slices/languageSlice";
import { syncCompanyDetails, syncDocumentConfig } from "@/utils/documentSync";
import {
  saveUserTypeToSession,
  loadUserTypeFromSession,
  clearUserTypeFromSession,
} from "@/utils/sessionStorageUtils";

// User type definitions (merged from UserTypeContext)
export type UserType =
  | "superadmin"
  | "admin"
  | "enduser"
  | "manager"
  | "unknown";

/**
 * Maps feature_id from API to user type
 * @param featureId - The feature_id value from API response
 * @returns UserType
 */
export const mapFeatureIdToUserType = (featureId: number): UserType => {
  switch (featureId) {
    case 1:
      return "superadmin";
    case 2:
      return "admin";
    case 3:
      return "manager";
    case 4:
      return "enduser";
    default:
      return "unknown";
  }
};

/**
 * Maps user type to feature_id
 * @param userType - The user type
 * @returns feature_id number
 */
export const mapUserTypeToFeatureId = (userType: UserType): number => {
  switch (userType) {
    case "superadmin":
      return 1;
    case "admin":
      return 2;
    case "manager":
      return 3;
    case "enduser":
      return 4;
    default:
      return 0;
  }
};

// Interface for login data
interface LoginData {
  token: string;
  user: any;
  role?: string;
  permissions?: Permissions[];
  feature_name?: string;
}

// Custom hook for Redux-based authentication (merged with user type functionality)
export const useAuth = () => {
  const dispatch = useDispatch<AppThunkDispatch>();
  const { authenticatedCall: fetchPermissions } = useMemo(
    () => actionsHandler("getUserPermissions"),
    [],
  );
  
  // Selectors
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isLoading = useAppSelector(selectIsLoading);
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const permissions = useAppSelector(selectUserPermissions);
  const permissionObjects = useAppSelector(selectUserPermissionObjects);
  const isSystemAdmin = useAppSelector(selectIsSystemAdmin);
  const issuperUser = useAppSelector(selectIssuperUser);

  // State for sessionStorage fallback values
  const [fallbackUserType, setFallbackUserType] = useState<UserType | null>(null);
  const [fallbackUserId, setFallbackUserId] = useState<number | undefined>(undefined);
  const [fallbackTimezoneId, setFallbackTimezoneId] = useState<string | undefined>(undefined);
  const [fallbackDisplayName, setFallbackDisplayName] = useState<string | undefined>(undefined);

  // Initialize from sessionStorage on mount (fallback when Redux is not ready)
  useEffect(() => {
    const storedData = loadUserTypeFromSession();
    if (storedData && !user?.feature_id) {
      // Only use fallback if Redux doesn't have user data yet
      setFallbackUserType(storedData.userType);
      setFallbackUserId(storedData.userId);
      setFallbackTimezoneId(storedData.timezoneId);
      setFallbackDisplayName(storedData.displayName);
    }
  }, []); // Run only once on mount

  // Derive user type properties from user data (with sessionStorage fallback)
  const userType: UserType = useMemo(() => {
    if (user?.feature_id) {
      return mapFeatureIdToUserType(user.feature_id);
    }
    // Fallback to sessionStorage data if Redux is not ready
    return fallbackUserType || "unknown";
  }, [user?.feature_id, fallbackUserType]);

  const userId = useMemo(() => {
    if (user?.id) return Number(user.id);
    // Fallback to sessionStorage data
    return fallbackUserId;
  }, [user?.id, fallbackUserId]);

  const timezoneId = useMemo(() => {
    if (user?.timezone_id) return user.timezone_id;
    // Fallback to sessionStorage data
    return fallbackTimezoneId;
  }, [user?.timezone_id, fallbackTimezoneId]);

  const displayName = useMemo(() => {
    if (user?.name) return user.name;
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    // Fallback to sessionStorage data
    return fallbackDisplayName;
  }, [user?.name, user?.first_name, user?.last_name, fallbackDisplayName]);

  // User type boolean flags
  const isSuperAdmin = userType === "superadmin";
  const isAdmin = userType === "admin";
  const isEndUser = userType === "enduser";
  const isManager = userType === "manager";

  // Sync user type to sessionStorage for tab-isolated persistence
  useEffect(() => {
    if (user && user.feature_id) {
      // Redux has user data - save it and clear fallback state
      saveUserTypeToSession(
        userType,
        user.feature_id,
        userId,
        timezoneId,
        displayName,
      );
      // Clear fallback state since Redux now has the data
      setFallbackUserType(null);
      setFallbackUserId(undefined);
      setFallbackTimezoneId(undefined);
      setFallbackDisplayName(undefined);
    } else if (!user || Object.keys(user).length === 0) {
      // User logged out - clear everything
      clearUserTypeFromSession();
      setFallbackUserType(null);
      setFallbackUserId(undefined);
      setFallbackTimezoneId(undefined);
      setFallbackDisplayName(undefined);
    }
  }, [user, userType, userId, timezoneId, displayName]);

  // Helper function to extract permission codes from API response
  const extractPermissionCodes = (
    apiResponse: ApiPermissionsResponse,
  ): Permissions[] => {
    const permissionCodes: Permissions[] = [];

    // Iterate through all modules in the response
    Object.values(apiResponse).forEach((modulePermissions) => {
      if (Array.isArray(modulePermissions)) {
        modulePermissions.forEach((permission) => {
          // Convert the code to a Permissions enum value if it exists
          const permissionCode = permission.code as Permissions;
          if (Object.values(Permissions).includes(permissionCode)) {
            permissionCodes.push(permissionCode);
          }
        });
      }
    });

    return permissionCodes;
  };

  // Login function - now returns a promise
  const login = useCallback(
    async (authData: LoginData): Promise<void> => {
      try {
        setAuth(authData);
        // Only fetch permissions if not already cached or user changed
        let apiPermissions: any = {};
        if (!permissions.length || user?.email !== authData.user.email) {
          const permissionsRes = await dispatch(
            fetchPermissions({
              method: "get",
            }),
          );
          apiPermissions = permissionsRes?.data || {};
        } else {
          // Use existing permissions from Redux state
          apiPermissions = permissionObjects;
        }

        const completeAuthData = {
          ...authData,
          permissions: apiPermissions,
          permissionObjects: {},
          issuperUser: authData.user.is_superuser,
          feature_name: authData.user.feature_name,
        };

        setAuth(completeAuthData);

        dispatch(
          loginSuccess({
            user: authData.user,
            token: authData.token,
            role: authData.role,
            permissions: apiPermissions,
            permissionObjects: {},
            issuperUser: authData.user.is_superuser,
            feature_name: authData.user.feature_name,
          }),
        );
        dispatch(setLanguage(authData.user.language));

        // Sync document configuration after successful login
        try {
          if (
            authData?.user?.is_superuser ||
            authData?.user?.feature_name === "ADMIN"
          ) {
            await syncDocumentConfig();
            await syncCompanyDetails();
          }
        } catch (error) {
          console.warn("Failed to sync document configuration:", error);
        }
      } catch (error) {
        console.error("❌ LOGIN PROCESS - Error during login:", error);
        throw error;
      }
    },
    [dispatch, permissions, permissionObjects, user?.email],
  );

  // Logout function
  const logout = useCallback(() => {
    // 1. Cancel all in-progress API requests
    cancelAllRequests();

    // 2. Capture user ID before clearing for cross-tab synchronization
    const currentUserId = user?.id;

    // 3. Preserve logo, colors, theme, language, and version before clearing localStorage
    const preservedLogo = localStorage.getItem('appLogo');
    const preservedPrimaryColor = localStorage.getItem('appPrimaryColor');
    const preservedSecondaryColor = localStorage.getItem('appSecondaryColor');
    const preservedTheme = localStorage.getItem('finloc-theme');
    const preservedLanguage = localStorage.getItem('language');
    const preservedAppVersion = localStorage.getItem('finloc_app_version');

    // 4. Clear auth data from services
    clearAuth();
    localStorage.clear();
    sessionStorage.clear();

    // 5. Restore preserved keys after clearing
    if (preservedLogo) {
      localStorage.setItem('appLogo', preservedLogo);
    }
    if (preservedPrimaryColor) {
      localStorage.setItem('appPrimaryColor', preservedPrimaryColor);
    }
    if (preservedSecondaryColor) {
      localStorage.setItem('appSecondaryColor', preservedSecondaryColor);
    }
    if (preservedTheme) {
      localStorage.setItem('finloc-theme', preservedTheme);
    }
    if (preservedLanguage) {
      localStorage.setItem('language', preservedLanguage);
    }
    if (preservedAppVersion) {
      localStorage.setItem('finloc_app_version', preservedAppVersion);
    }

    // 6. Broadcast logout to other tabs
    if (currentUserId && typeof window !== "undefined") {
      localStorage.setItem(
        "finloc_logout_event",
        JSON.stringify({
          userId: currentUserId,
          timestamp: Date.now(),
        }),
      );
    }

    // 7. Dispatch logout action
    dispatch(logoutAction());

    // 8. Direct redirect to login
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  }, [dispatch, user?.id]);

  // Check if user has a specific permission
  const hasPermission = useCallback(
    (permission: Permissions): boolean => {
      const permissionsArray = Array.isArray(permissions) ? permissions : [];
      const hasAccess =
        permissionsArray.includes(permission) ||
        userRole === ADMIN_ROLE ||
        issuperUser;
      return hasAccess;
    },
    [permissions, userRole, issuperUser],
  );

  const getUserPermissions = useCallback((): Permissions[] => {
    return Array.isArray(permissions) ? permissions : [];
  }, [permissions]);

  // Function to manually set user type from feature_id (for compatibility)
  const setUserTypeFromFeatureId = useCallback(
    (
      featureId: number,
      userIdParam?: number,
      timezoneIdParam?: string,
      firstName?: string,
      lastName?: string,
    ) => {
      const type = mapFeatureIdToUserType(featureId);
      const fullName =
        firstName && lastName ? `${firstName} ${lastName}` : undefined;
      saveUserTypeToSession(
        type,
        featureId,
        userIdParam,
        timezoneIdParam,
        fullName,
      );
    },
    [],
  );

  return {
    // Authentication state
    isAuthenticated,
    isLoading,
    user,
    userRole,
    permissions,
    permissionObjects,
    isSystemAdmin,
    issuperUser,
    // User type properties (merged from UserTypeContext)
    userType,
    userId,
    timezoneId,
    displayName,
    isSuperAdmin,
    isAdmin,
    isEndUser,
    isManager,
    // Actions
    login,
    logout,
    syncDocumentConfig,
    setUserTypeFromFeatureId,
    // Permission checks
    hasPermission,
    getUserPermissions,
  };
};
